import { sql } from 'drizzle-orm';
import { drizzle } from 'drizzle-orm/sqlite-proxy';

import type { WorkerResponse } from './db.worker';
import migrations from './drizzle/migrations';
import * as schema from './schema';
import type { DrizzleDB, InitDBOptions, InitDBResult } from './types';

export const getDbName = (userId: number): string => {
  return `factiii_db_${userId}.sqlite3`;
};

/**
 * Creates a worker and provides a typed request/response helper.
 * The worker runs wa-sqlite + OPFSCoopSyncVFS for OPFS persistence
 * without requiring SharedArrayBuffer or COEP/COOP headers.
 */
function createWorkerBridge() {
  const worker = new Worker(new URL('./db.worker.ts', import.meta.url), {
    type: 'module'
  });

  let nextId = 0;
  const pending = new Map<
    number,
    { resolve: (v: { rows: unknown[][] }) => void; reject: (e: Error) => void }
  >();

  worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
    const msg = e.data;
    const p = pending.get(msg.id);
    if (!p) return;
    pending.delete(msg.id);

    if ('error' in msg) {
      p.reject(new Error(msg.error));
    } else {
      p.resolve(msg.result);
    }
  };

  worker.onerror = (e) => {
    // Reject all pending requests on unrecoverable worker error
    const err = new Error(`Worker error: ${e.message}`);
    for (const [, p] of pending) {
      p.reject(err);
    }
    pending.clear();
  };

  function send(msg: Record<string, unknown>): Promise<{ rows: unknown[][] }> {
    return new Promise((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { resolve, reject });
      worker.postMessage({ ...msg, id });
    });
  }

  return { worker, send };
}

/**
 * Browser-compatible migration runner.
 * Follows the same __drizzle_migrations table schema as the official migrators.
 */
async function applyMigrations(db: DrizzleDB): Promise<void> {
  await db.run(sql`
    CREATE TABLE IF NOT EXISTS \`__drizzle_migrations\` (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      hash text NOT NULL,
      created_at numeric
    )
  `);

  const applied = await db.values<[number, string, number]>(
    sql`SELECT id, hash, created_at FROM \`__drizzle_migrations\` ORDER BY created_at DESC LIMIT 1`
  );
  const lastApplied = applied[0] ?? undefined;

  for (const entry of migrations.journal.entries) {
    if (lastApplied && Number(lastApplied[2]) >= entry.when) continue;
    const key = `m${String(entry.idx).padStart(4, '0')}`;
    const migrationSql = (migrations.migrations as Record<string, string>)[key];
    if (!migrationSql) throw new Error(`Missing migration: ${entry.tag}`);

    const statements = migrationSql
      .split('--> statement-breakpoint')
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 0);

    // Run each migration as a transaction so it either fully applies or rolls back
    await db.run(sql`BEGIN`);
    try {
      for (const statement of statements) {
        await db.run(sql.raw(statement));
      }
      await db.run(
        sql`INSERT INTO \`__drizzle_migrations\` ("hash", "created_at") VALUES(${entry.tag}, ${entry.when})`
      );
      await db.run(sql`COMMIT`);
    } catch (e) {
      await db.run(sql`ROLLBACK`);
      throw e;
    }
  }
}

export const initializeDB = async (
  userId: number,
  // Web never defers migrations — accepted for signature parity with mobile.
  _options?: InitDBOptions
): Promise<InitDBResult> => {
  const dbName = getDbName(userId);
  const { worker, send } = createWorkerBridge();

  try {
    // Initialize wa-sqlite + OPFSCoopSyncVFS in the worker
    await send({ type: 'init', dbName });

    const db = drizzle(
      (querySql, params, method) => {
        return send({
          type: 'query',
          sql: querySql,
          params: params as unknown[],
          method
        });
      },
      async (queries) => {
        const results: { rows: unknown[][] }[] = [];
        for (const q of queries) {
          const result = await send({
            type: 'query',
            sql: q.sql,
            params: q.params as unknown[],
            method: q.method
          });
          results.push(result);
        }
        return results;
      },
      { schema }
    ) as unknown as DrizzleDB;

    // Enable foreign key enforcement
    await db.run(sql`PRAGMA foreign_keys = ON`);

    await applyMigrations(db);

    return {
      db,
      destroy: () => {
        send({ type: 'close' }).catch(() => {});
        worker.terminate();
      },
      pendingMigrations: false
    };
  } catch (error) {
    send({ type: 'close' }).catch(() => {});
    worker.terminate();
    throw error;
  }
};

export const manuallyDeleteDatabase = async (userId: number): Promise<void> => {
  const dbName = getDbName(userId);

  try {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(dbName);
  } catch (error) {
    console.warn('[DB] Could not delete OPFS file:', error);
  }

  // Also delete the OPFS blob directory
  try {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(`factiii_blobs_${userId}`, { recursive: true });
  } catch (error) {
    console.warn('[DB] Could not delete OPFS blob directory:', error);
  }
};
